/**
 * The siding colours, and when a customer is shown them.
 *
 * Westlake Parkview vinyl siding comes in eighteen colours, and the chart is a
 * viewer of the same kind as the metal roofs' (`ColorViewer` in
 * `shingleColors.ts`), from the design handoff Jonathon supplied on
 * 2026-09-30. Each slide is a close-up of the Dutch Lap, Double 5" profile in
 * one colour, and the last is a grid of all eighteen.
 *
 * It is matched on the product, the way gutters are: siding is never what a
 * package's roof is made of, so it is looked for on every line the document
 * offers, in a package or as an upgrade. Jonathon, 2026-09-30, asked which
 * estimates should show it: "Only that one product". So the match is the one
 * product he made for it, Standard Vinyl Siding, rather than any line that
 * says vinyl siding; a second vinyl product gets a lineup of its own.
 *
 * The photographs are Westlake's, captured from their site on 2026-09-30.
 * Jonathon confirmed the same day that we may use them.
 */

import type { ColorSlide, ColorViewer } from "./shingleColors.js";

/** One siding product, with the viewer it opens. */
export interface SidingLine {
  /** Stable: what `estimates.hidden_colors` stores to switch the card off. */
  key: string;
  /** What the card names under "Siding". */
  product: string;
  /** What identifies this product on a document. */
  matches: RegExp[];
  viewer: ColorViewer;
}

/**
 * Parkview's eighteen, in the manufacturer's order, with the manufacturer's
 * names and hex values. Never renamed or reordered.
 *
 * The paths are frozen into a proposal's snapshot at send (ADR 0032), so a
 * file under `apps/web/public/siding-colors` is never renamed or removed once
 * it has shipped (ADR 0237). `siding-colors-manifest.json` beside the viewer
 * lists every one.
 */
const PARKVIEW: readonly [file: string, name: string, hex: string][] = [
  ["country-beige", "Country Beige", "#C4B6A2"],
  ["wheat", "Wheat", "#EFDAB3"],
  ["aspen-white", "Aspen White", "#FFFFFF"],
  ["cypress", "Cypress", "#8F8C78"],
  ["pearl", "Pearl", "#E4D7C6"],
  ["mocha", "Mocha", "#C0B6A7"],
  ["saddle", "Saddle", "#D3BEA1"],
  ["clay", "Clay", "#ABA28B"],
  ["bone", "Bone", "#CEC6BA"],
  ["greystone", "Greystone", "#8B8882"],
  ["rye", "Rye", "#B1A693"],
  ["driftwood", "Driftwood", "#BCB6B1"],
  ["polaris", "Polaris", "#778D99"],
  ["canyon", "Canyon", "#74655A"],
  ["oceanside", "Oceanside", "#838A92"],
  ["nantucket", "Nantucket", "#8A8F92"],
  ["smoky-ash", "Smoky Ash", "#857E75"],
  ["metropolitan-gray", "Metropolitan Gray", "#777A74"],
];

const PARKVIEW_VIEWER: ColorViewer = {
  eyebrow: "Parkview · Vinyl Siding",
  title: "Siding Colors",
  slides: PARKVIEW.map(([file, name, hex]): ColorSlide => ({
    src: `/siding-colors/parkview/${file}.jpg`,
    name,
    hex,
  })),
  grid: {
    heading: `All ${PARKVIEW.length} Parkview Colors`,
    disclaimer:
      "Colors shown online and in printed materials may vary from actual colors. Review color fan decks or real product samples before making a final color selection.",
  },
};

/**
 * The product Jonathon made for James Jordan's job, by its name. Jonathon,
 * 2026-09-30: "just change the name to standard vinyl siding".
 *
 * The whole name and nothing else, so a line that merely mentions vinyl
 * siding does not open Parkview's colours. A document carries the name each
 * line was given, not the product's id, which is why it is matched this way.
 */
const PARKVIEW_PRODUCT = /^\s*standard vinyl siding\s*$/i;

export const SIDING_LINES: readonly SidingLine[] = [
  {
    key: "parkview",
    product: "Standard Vinyl Siding",
    matches: [PARKVIEW_PRODUCT],
    viewer: PARKVIEW_VIEWER,
  },
];

/** The siding lineup any of these names carries, first match wins. */
export function sidingLineFor(
  names: readonly (string | null | undefined)[],
): SidingLine | null {
  for (const name of names) {
    if (!name || name.trim() === "") continue;
    const line = SIDING_LINES.find((candidate) =>
      candidate.matches.some((pattern) => pattern.test(name)),
    );
    if (line) return line;
  }
  return null;
}
